import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { authFetch } from '../utils/api'

export const useUiStore = defineStore('ui', () => {
  const sidebarOpen = ref(true)
  const mobileNavOpen = ref(false)
  const theme = ref<'light' | 'dark' | 'system'>(
    (localStorage.getItem('wiki-theme') as 'light' | 'dark' | 'system') || 'system'
  )
  const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200)

  const isMobile = computed(() => windowWidth.value < 640)
  const isTablet = computed(() => windowWidth.value >= 640 && windowWidth.value < 1024)
  const isDesktop = computed(() => windowWidth.value >= 1024)
  const showBottomNav = computed(() => !isDesktop.value)

  let resizeHandler: (() => void) | null = null

  const init = () => {
    resizeHandler = () => {
      windowWidth.value = window.innerWidth
      if (window.innerWidth >= 1024) {
        sidebarOpen.value = true
        mobileNavOpen.value = false
      } else {
        sidebarOpen.value = false
      }
    }
    window.addEventListener('resize', resizeHandler)
    applyTheme(theme.value)
    loadUserPreferences()
  }

  const cleanup = () => {
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler)
    }
  }

  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value
  }

  const toggleMobileNav = () => {
    mobileNavOpen.value = !mobileNavOpen.value
  }

  const applyTheme = (t: 'light' | 'dark' | 'system') => {
    theme.value = t
    localStorage.setItem('wiki-theme', t)

    const isDark = t === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : t === 'dark'

    if (t === 'system') {
      document.documentElement.removeAttribute('data-theme')
    } else {
      document.documentElement.setAttribute('data-theme', t)
    }

    setFavicon(isDark)
    syncThemeToServer(t)
  }

  const loadUserPreferences = async () => {
    const auth = useAuthStore()
    if (!auth.isAuthenticated) return

    try {
      const response = await authFetch('/api/user/settings', {
        headers: {}
      })
      if (response.ok) {
        const data = await response.json()
        if (data.settings?.theme && !localStorage.getItem('wiki-theme')) {
          applyTheme(data.settings.theme as 'light' | 'dark' | 'system')
        }
      }
    } catch {
      // Silently fail — localStorage is the fallback
    }
  }

  const syncThemeToServer = async (t: string) => {
    const auth = useAuthStore()
    if (!auth.isAuthenticated) return

    try {
      await authFetch('/api/user/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: { theme: t } })
      })
    } catch {
      // Silently fail — localStorage is the fallback
    }
  }

  const setFavicon = (dark: boolean) => {
    const color = dark ? '#60a5fa' : '#3b82f6'
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><text x="32" y="36" font-family="Arial" font-size="32" font-weight="bold" fill="${color}" text-anchor="middle">G</text><text x="16" y="12" font-family="Arial" font-size="32" font-weight="bold" fill="${color}" text-anchor="middle" transform="rotate(180, 16, 12)">G</text></svg>`
    const link = document.querySelector("link[rel~='icon']") || document.createElement('link')
    link.rel = 'icon'
    link.href = 'data:image/svg+xml,' + encodeURIComponent(svg)
    document.head.appendChild(link)
  }

  return {
    sidebarOpen,
    mobileNavOpen,
    theme,
    windowWidth,
    isMobile,
    isTablet,
    isDesktop,
    showBottomNav,
    init,
    cleanup,
    toggleSidebar,
    toggleMobileNav,
    applyTheme,
  }
})
